import { create } from 'zustand';

interface UIState {
  // Sidebar state
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  
  // Modal state
  isViewOnly: boolean;
  setViewOnly: (isView: boolean) => void;

  // Callback functions for refreshing data after modal operations
  refreshCallbacks: {
    cuttingOrders?: () => void;
    materials?: () => void;
    suppliers?: () => void;
    customers?: () => void;
    services?: () => void;
    receiptVouchers?: () => void;
    paymentVouchers?: () => void;
    categories?: () => void;
    orders?: () => void;
    priceLists?: () => void;
    cuttingSizes?: () => void;
    cuttingConfigs?: () => void;
    invoices?: () => void;
    discountVouchers?: () => void;
  };
  setRefreshCallback: (key: keyof UIState['refreshCallbacks'], callback: () => void) => void;
  clearRefreshCallback: (key: keyof UIState['refreshCallbacks']) => void;

  modals: {
    createSupplier: boolean;
    editSupplier: boolean;
    supplierDetail: boolean;
    payment: boolean;
    discount: boolean;
    exportDebt: boolean;
    importSupplier: boolean;
    exportSupplier: boolean;
    createCategory: boolean;
    categoryDetail: boolean;
    createMaterial: boolean;
    materialDetail: boolean;
    importMaterial: boolean;
    exportMaterial: boolean;
    createCuttingOrder: boolean;
    cuttingOrderDetail: boolean;
    cuttingConfig: boolean;
    cutConfigLine: boolean;
    cuttingFormLine: boolean;
    createCustomer: boolean;
    editCustomer: boolean;
    customerDetail: boolean;
    importCustomer: boolean;
    exportCustomer: boolean;
    createPriceList: boolean;
    priceListDetail: boolean;
    createService: boolean;
    editService: boolean;
    serviceDetail: boolean;
    importService: boolean;
    exportService: boolean;
    createCuttingSize: boolean;
    editCuttingSize: boolean;
    viewCuttingSize: boolean;
    createCuttingConfig: boolean;
    editCuttingConfig: boolean;
    viewCuttingConfig: boolean;
  };
  openModal: (modalName: keyof UIState['modals'], data?: any) => void;
  closeModal: (modalName: keyof UIState['modals']) => void;
  closeAllModals: () => void;
  modalData: any;
  setModalData: (data: any) => void;
  
  // Selected items
  selectedSupplier: string | null;
  setSelectedSupplier: (id: string | null) => void;
  editingSupplier: any | null;
  setEditingSupplier: (supplier: any | null) => void;
  selectedCustomer: string | null;
  setSelectedCustomer: (id: string | null) => void;
  editingCustomer: any | null;
  setEditingCustomer: (customer: any | null) => void;
  selectedCategory: string | null;
  setSelectedCategory: (id: string | null) => void;
  selectedMaterial: string | null;
  setSelectedMaterial: (id: string | null) => void;
  selectedCuttingOrder: string | null;
  setSelectedCuttingOrder: (id: string | null) => void;
  
  // Language
  language: string;
  setLanguage: (lang: string) => void;
  
  // Theme
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Sidebar state
  isSidebarCollapsed: false,
  toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),
  
  // Modal state
  isViewOnly: false,
  setViewOnly: (isView) => set({ isViewOnly: isView }),

  // Refresh callbacks
  refreshCallbacks: {},
  setRefreshCallback: (key, callback) => set((state) => ({
    refreshCallbacks: { ...state.refreshCallbacks, [key]: callback }
  })),
  clearRefreshCallback: (key) => set((state) => ({
    refreshCallbacks: { ...state.refreshCallbacks, [key]: undefined }
  })),
  modals: {
    createSupplier: false,
    editSupplier: false,
    supplierDetail: false,
    payment: false,
    discount: false,
    exportDebt: false,
    importSupplier: false,
    exportSupplier: false,
    createCategory: false,
    categoryDetail: false,
    createMaterial: false,
    materialDetail: false,
    importMaterial: false,
    exportMaterial: false,
    createCuttingOrder: false,
    cuttingOrderDetail: false,
    cuttingConfig: false,
    cutConfigLine: false,
    cuttingFormLine: false,
    createCustomer: false,
    editCustomer: false,
    customerDetail: false,
    importCustomer: false,
    exportCustomer: false,
    createPriceList: false,
    priceListDetail: false,
    createService: false,
    editService: false,
    serviceDetail: false,
    importService: false,
    exportService: false,
    createCuttingSize: false,
    editCuttingSize: false,
    viewCuttingSize: false,
    createCuttingConfig: false,
    editCuttingConfig: false,
    viewCuttingConfig: false,
  },
  openModal: (modalName, data) => set((state) => ({
    modals: { ...state.modals, [modalName]: true },
    modalData: data || null
  })),
  closeModal: (modalName) => set((state) => {
    // Only clear modalData if no other modals are open
    const updatedModals = { ...state.modals, [modalName]: false };
    const hasOpenModals = Object.values(updatedModals).some(isOpen => isOpen);

    return {
      modals: updatedModals,
      modalData: hasOpenModals ? state.modalData : null,
      isViewOnly: hasOpenModals ? state.isViewOnly : false,
    };
  }),
  closeAllModals: () => set({
    modals: Object.keys(get().modals).reduce((acc, key) => {
      acc[key as keyof UIState['modals']] = false;
      return acc;
    }, {} as UIState['modals']),
    isViewOnly: false,
  }),
  
  // Selected items
  selectedSupplier: null,
  setSelectedSupplier: (id) => set({ selectedSupplier: id }),
  editingSupplier: null,
  setEditingSupplier: (supplier) => set({ editingSupplier: supplier }),
  selectedCustomer: null,
  setSelectedCustomer: (id) => set({ selectedCustomer: id }),
  editingCustomer: null,
  setEditingCustomer: (customer) => set({ editingCustomer: customer }),
  selectedCategory: null,
  setSelectedCategory: (id) => set({ selectedCategory: id }),
  selectedMaterial: null,
  setSelectedMaterial: (id) => set({ selectedMaterial: id }),
  selectedCuttingOrder: null,
  setSelectedCuttingOrder: (id) => set({ selectedCuttingOrder: id }),
  
  // Language
  language: localStorage.getItem('i18nextLng') || 'en',
  setLanguage: (lang) => {
    localStorage.setItem('i18nextLng', lang);
    set({ language: lang });
  },
  
  // Theme
  theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
  toggleTheme: () => {
    const newTheme = get().theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', newTheme);
    set({ theme: newTheme });
  },

  // Modal data
  modalData: null,
  setModalData: (data) => set({ modalData: data }),
}));
